import { useEffect, useState } from 'react'
import moment from 'moment'
import { useQuery } from '@apollo/client'
import { USER_PRIORITIES } from 'Apollo/Queries/UserPriorities'
import { IMPACT_DASHBOARD_GOALS } from 'Apollo/Queries/ImpactDashboardGoals'
import { IMPACT_DASHBOARD_ACADEMY_ACTIVITY } from 'Apollo/Queries/ImpactDashboardAcademyActivity'
import { useAppState } from 'Context/AppContext/AppContext'
import {
  Goal,
  ImpactDashboardActivity,
  UserPrioritiesQuery,
  UserSurveyPriority,
  ImpactDashboardGoalsQuery,
} from 'Generated/graphql-types'
import { NavigationTabs } from '@resiliatech/birdsong'
import { useHistory } from 'react-router-dom'
import { GoalProgress } from './GoalProgress/GoalProgress'
import { TaskDigest } from './TaskDigest/TaskDigest'
import { DashboardFilters } from './DashboardFilters/DashboardFilters'
import { FilterArgs, ResourceIdToTaskIdMap } from './types'
import { PriorityProgress } from './PriorityProgress/PriorityProgress'
import { StoriesMetrics } from './StoriesMetrics/StoriesMetrics'
import { PRIORTIES_FILTER_TRIGGER, getResourceCounts } from './utils'
import { AcademyDigest } from './AcademyDigest/AcademyDigest'
import { AcademyMetrics } from './AcademyDigest/AcademyMetrics'
import { dispatchClearFiltersClicked } from './analytics.utils'
import { GoalProgressUpdates } from './GoalProgressUpdates/GoalProgressUpdates'
import {
  ImpactReportDashboardContainer,
  SectionHeader,
  TwoWidgetContainer,
} from './ImpactReportDashboard.styled'

const NINETY_DAYS_AGO = moment.utc().subtract(90, 'days').toISOString()

const ImpactReportDashboard = () => {
  const {
    nonprofitOrgConfig: { orgId: nonprofitOrgId, platformPlan, funderOrgId },
    userConfig: { id: userId, email: userEmail },
  } = useAppState()
  const [isPriorityFilterChecked, setIsPriorityFilterChecked] = useState(false)
  const [categories, setCategories] = useState<string[]>([])
  const [taskOwnerId, setTaskOwnerId] = useState('')
  const [goalTitleValues, setGoalTitleValues] = useState<string[]>([])
  const history = useHistory()

  const { data: { userSurveyPriorities = [] as UserSurveyPriority[] } = {} } =
    useQuery<UserPrioritiesQuery>(USER_PRIORITIES, {
      variables: { userId },
    })

  const { data: { goals = [] as Goal[] } = {}, loading: goalsLoading } =
    useQuery<ImpactDashboardGoalsQuery & { goals: Goal[] }>(
      IMPACT_DASHBOARD_GOALS,
      {
        variables: {
          activeOnly: true,
          createdAtStart: NINETY_DAYS_AGO,
          nonprofitOrgId,
        },
      }
    )

  const { data, loading: academyLoading } = useQuery(
    IMPACT_DASHBOARD_ACADEMY_ACTIVITY,
    {
      variables: {
        nonprofitOrgId,
      },
    }
  )

  const userResourceActivity = (data?.impactDashboardAcademyActivity ||
    []) as ImpactDashboardActivity[]

  const resourceIdToTaskIdMap = goals.reduce((acc, goal) => {
    goal.ChecklistItem.forEach((item) => {
      if (item.resourceId) acc[item.resourceId] = item.id
    })
    return acc
  }, {} as ResourceIdToTaskIdMap)

  const userPriorities = userSurveyPriorities as UserSurveyPriority[]
  const priorityCategories = userSurveyPriorities.map(
    (priority) => priority.category
  )

  // Check if category filters and user priorities are exact match for the priority filter "checked" value
  useEffect(() => {
    if (
      priorityCategories.length === categories.length &&
      // Check if the arrays are equal, regardless of order
      priorityCategories.every((priority) => categories.includes(priority))
    ) {
      setIsPriorityFilterChecked(true)
    } else {
      setIsPriorityFilterChecked(false)
    }
  }, [categories, priorityCategories, userSurveyPriorities])

  const handleResetFilters = () => {
    setCategories([])
    setTaskOwnerId('')
    setIsPriorityFilterChecked(false)
    setGoalTitleValues([])
    dispatchClearFiltersClicked({
      funderOrgId: funderOrgId || null,
      orgId: nonprofitOrgId,
      platformPlan: platformPlan || '',
      userId,
      userEmail,
    })
  }

  const handleFilterChange = ({ filterType, value }: FilterArgs) => {
    switch (filterType) {
      case 'categories': {
        // Set categories to user's priorities and nothing else
        if ((value as string[]).includes(PRIORTIES_FILTER_TRIGGER)) {
          setCategories(priorityCategories)
          break
        }
        // Or set categories to the selected values
        setCategories(value as string[])
        break
      }
      case 'taskOwnerId':
        setTaskOwnerId(value as string)
        break
      case 'goalTitleValues':
        setGoalTitleValues(value as string[])
        break
      default:
        break
    }
  }

  const uniqueGoalTitles = Array.from(new Set(goals.map((goal) => goal.title)))
  const filters = { categories, taskOwnerId, goalTitleValues }

  const { totalResourcesViewed } = getResourceCounts(userResourceActivity)

  const hasData = userResourceActivity.length > 0 && totalResourcesViewed > 0
  const goalsDashboardTabs = [
    {
      label: 'Goals',
      path: '/impact/goals',
    },
    {
      label: 'Templates',
      path: '/impact/templates',
    },
    {
      label: 'Dashboard',
      path: '/impact/dashboard',
    },
  ]
  const onTabChange = (newValue: number) => {
    history.push({
      pathname: goalsDashboardTabs[newValue].path,
    })
  }

  return (
    <ImpactReportDashboardContainer>
      <NavigationTabs
        tabs={goalsDashboardTabs}
        selectedTab={2}
        onTabChange={onTabChange}
        title='Goals'
      />
      <DashboardFilters
        filterValues={{
          categories,
          taskOwnerId,
          goalTitleValues,
        }}
        goalTitles={uniqueGoalTitles}
        isPriorityFilterChecked={isPriorityFilterChecked}
        onFilterChange={handleFilterChange}
        onResetFilters={handleResetFilters}
        userPriorities={userPriorities}
      />
      <PriorityProgress
        goals={goals}
        loading={goalsLoading}
        userResourceActivity={userResourceActivity}
        userSurveyPriorities={userSurveyPriorities}
      />
      <SectionHeader>Impact</SectionHeader>
      <TaskDigest filters={filters} loading={goalsLoading} goals={goals} />
      <TwoWidgetContainer>
        <GoalProgress
          filters={filters}
          goals={goals}
          loading={goalsLoading}
          priorities={userPriorities}
        />
        <GoalProgressUpdates />
      </TwoWidgetContainer>
      <StoriesMetrics />
      <SectionHeader>Academy</SectionHeader>
      <AcademyMetrics
        loading={academyLoading}
        userResourceActivity={userResourceActivity}
      />
      {hasData && (
        <AcademyDigest
          loading={academyLoading}
          userResourceActivity={userResourceActivity}
          resourceIdToTaskIdMap={resourceIdToTaskIdMap}
        />
      )}
    </ImpactReportDashboardContainer>
  )
}

export default ImpactReportDashboard
